/*
 * Auth context for the K-Stack admin console.
 *
 * Simpler than AquaStar Admin because there's no per-app staff role
 * lookup — this console is for platform operators only.
 *
 * State: { me, status, error }
 *   - me: the result of GET /auth/me, or null
 *   - status: 'unknown' | 'guest' | 'authed' | 'denied'
 *       'unknown' — booted, haven't pinged /auth/me yet (show splash)
 *       'guest'   — no token (show login)
 *       'authed'  — token + is_platform_admin === true (let them in)
 *       'denied'  — token works but user is not a platform admin (show denial)
 *       'stepup'  — a platform admin whose admin power needs a passkey step-up
 *                   first (NEXUS-586: adminMfa.enforced && !adminMfa.satisfied)
 *
 * Gate is server-side: Nexus's /auth/me returns is_platform_admin
 * derived from the users table. We just reflect it in UI state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  type AuthMe,
  getMe,
  getToken,
  login as apiLogin,
  logout as apiLogout,
  setRefreshToken,
  setToken,
} from './apiClient';

export type AuthStatus = 'unknown' | 'guest' | 'authed' | 'denied' | 'stepup';

interface AuthContextValue {
  status: AuthStatus;
  me: AuthMe | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
  /** NEXUS-586: store a token minted by a passkey step-up, then re-read /auth/me. */
  adoptToken: (accessToken: string) => Promise<void>;
  /** NEXUS-586: store a whole session (passkey sign-in), then re-read /auth/me. */
  adoptSession: (accessToken: string, refreshToken: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [me, setMe] = useState<AuthMe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastCheckAt = useRef(Date.now());

  const refresh = useCallback(async () => {
    lastCheckAt.current = Date.now();
    if (!getToken()) {
      setMe(null);
      setStatus('guest');
      return;
    }
    try {
      const profile = await getMe();
      setMe(profile);
      setStatus(
        !profile.isPlatformAdmin
          ? 'denied'
          : profile.adminMfa?.enforced && !profile.adminMfa.satisfied
            ? 'stepup'
            : 'authed',
      );
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
        setStatus('guest');
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : String(e));
        setStatus('guest');
      }
    }
  }, []);

  // Bootstrap on mount.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // NEXUS-586: re-check /auth/me when the console comes back to the foreground after
  // more than 5 minutes — a step-up may have expired meanwhile (→ status 'stepup').
    useEffect(() => {
    const STALE_MS = 5 * 60_000;
    const maybe = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastCheckAt.current < STALE_MS) return;
      lastCheckAt.current = Date.now();
      if (getToken()) void refresh();
    };
    window.addEventListener('focus', maybe);
    document.addEventListener('visibilitychange', maybe);
    return () => {
      window.removeEventListener('focus', maybe);
      document.removeEventListener('visibilitychange', maybe);
    };
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        await apiLogin(email, password);
        await refresh();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        throw e;
      }
    },
    [refresh],
  );

  const signOut = useCallback(() => {
    apiLogout();
    setMe(null);
    setStatus('guest');
    setError(null);
  }, []);

  const adoptToken = useCallback(
    async (accessToken: string) => {
      setToken(accessToken);
      await refresh();
    },
    [refresh],
  );

  const adoptSession = useCallback(
    async (accessToken: string, refreshToken: string | null) => {
      setToken(accessToken);
      setRefreshToken(refreshToken);
      await refresh();
    },
    [refresh],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, error, signIn, signOut, refresh, adoptToken, adoptSession }),
    [status, me, error, signIn, signOut, refresh, adoptToken, adoptSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
